import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  CreateMapNoteTemplateInput,
  CreateMapNoteTemplateEntryInput,
  MapNoteTemplate,
  MapNoteTemplateEntry,
  MapNoteTemplateSummary,
  UpdateMapNoteTemplateInput,
  UpdateMapNoteTemplateEntryInput,
} from "@west-marches/shared";

import {
  createMapNoteTemplate,
  createMapNoteTemplateEntry,
  createTemplateFromMapNote,
  deleteMapNoteTemplate,
  deleteMapNoteTemplateEntry,
  getMapNoteTemplate,
  listMapNoteTemplates,
  updateMapNoteTemplate,
  updateMapNoteTemplateEntry,
} from "@/lib/mapNoteTemplateClient";
import { queryKeys } from "@/lib/queryKeys";

export function useMapNoteTemplateListQuery(enabled = true) {
  return useQuery({
    queryKey: queryKeys.mapNoteTemplates.list(),
    queryFn: () => listMapNoteTemplates(),
    enabled,
  });
}

export function useMapNoteTemplateQuery(templateId: string | null) {
  return useQuery({
    queryKey: queryKeys.mapNoteTemplates.detail(templateId ?? ""),
    queryFn: () => getMapNoteTemplate(templateId!),
    enabled: Boolean(templateId),
  });
}

export function useCreateMapNoteTemplateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMapNoteTemplateInput) => createMapNoteTemplate(input),
    onSuccess(template) {
      queryClient.setQueryData<MapNoteTemplateSummary[]>(
        queryKeys.mapNoteTemplates.list(),
        (current) => [...(current ?? []), template],
      );
    },
    onError(error) {
      Sentry.captureException(error, { tags: { area: "map-note-template", op: "create" } });
    },
  });
}

export function useUpdateMapNoteTemplateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      templateId,
      input,
    }: {
      templateId: string;
      input: UpdateMapNoteTemplateInput;
    }) => updateMapNoteTemplate(templateId, input),
    onSuccess(template) {
      queryClient.setQueryData<MapNoteTemplateSummary[]>(
        queryKeys.mapNoteTemplates.list(),
        (current) => (current ?? []).map((t) => (t.id === template.id ? template : t)),
      );
      queryClient.setQueryData<MapNoteTemplate | undefined>(
        queryKeys.mapNoteTemplates.detail(template.id),
        (current) => (current ? { ...current, ...template } : current),
      );
    },
    onError(error, variables) {
      Sentry.captureException(error, {
        tags: { area: "map-note-template", op: "update" },
        extra: { templateId: variables.templateId },
      });
    },
  });
}

export function useDeleteMapNoteTemplateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => deleteMapNoteTemplate(templateId),
    onSuccess(_, templateId) {
      queryClient.setQueryData<MapNoteTemplateSummary[]>(
        queryKeys.mapNoteTemplates.list(),
        (current) => (current ?? []).filter((t) => t.id !== templateId),
      );
      queryClient.removeQueries({ queryKey: queryKeys.mapNoteTemplates.detail(templateId) });
    },
    onError(error, templateId) {
      Sentry.captureException(error, {
        tags: { area: "map-note-template", op: "delete" },
        extra: { templateId },
      });
    },
  });
}

export function useCreateTemplateFromMapNoteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignId,
      mapNoteId,
      name,
    }: {
      campaignId: string;
      mapNoteId: string;
      name: string;
    }) => createTemplateFromMapNote(campaignId, mapNoteId, name),
    onSuccess(template) {
      queryClient.setQueryData<MapNoteTemplateSummary[]>(
        queryKeys.mapNoteTemplates.list(),
        (current) => [...(current ?? []), template],
      );
    },
    onError(error, variables) {
      Sentry.captureException(error, {
        tags: { area: "map-note-template", op: "from-map-note" },
        extra: { campaignId: variables.campaignId, mapNoteId: variables.mapNoteId },
      });
    },
  });
}

export function useCreateMapNoteTemplateEntryMutation(templateId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMapNoteTemplateEntryInput) =>
      createMapNoteTemplateEntry(templateId!, input),
    onSuccess(entry) {
      queryClient.setQueryData<MapNoteTemplate | undefined>(
        queryKeys.mapNoteTemplates.detail(entry.templateId),
        (current) => (current ? { ...current, entries: [...current.entries, entry] } : current),
      );
    },
  });
}

export function useUpdateMapNoteTemplateEntryMutation(templateId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      input,
    }: {
      entryId: string;
      input: UpdateMapNoteTemplateEntryInput;
    }) => updateMapNoteTemplateEntry(templateId!, entryId, input),
    onSuccess(entry) {
      queryClient.setQueryData<MapNoteTemplate | undefined>(
        queryKeys.mapNoteTemplates.detail(entry.templateId),
        (current) =>
          current
            ? {
                ...current,
                entries: current.entries.map((e: MapNoteTemplateEntry) =>
                  e.id === entry.id ? entry : e,
                ),
              }
            : current,
      );
    },
  });
}

export function useDeleteMapNoteTemplateEntryMutation(templateId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => deleteMapNoteTemplateEntry(templateId!, entryId),
    onSuccess(_, entryId) {
      if (!templateId) return;
      queryClient.setQueryData<MapNoteTemplate | undefined>(
        queryKeys.mapNoteTemplates.detail(templateId),
        (current) =>
          current
            ? {
                ...current,
                entries: current.entries.filter((e: MapNoteTemplateEntry) => e.id !== entryId),
              }
            : current,
      );
    },
  });
}
